/**
 * The writing field and the paper underneath it.
 *
 * Preact renders the frame here — the sheets, the wedge layer, the field — but
 * NOT the text in it. The field is a `contenteditable` whose content belongs
 * to the browser and to the child: caret, selection and undo hang on it. A
 * virtual DOM that rewrites the children breaks all three.
 *
 * `shouldComponentUpdate` is not needed for that: the field has no children in
 * JSX, so Preact has nothing it could reconcile. Everything that goes in comes
 * through `editor/text.ts`.
 *
 * The effects here are the measurements that have to run after the layout:
 * measure the font, calculate the baseline, count the sheets, place the
 * wedges. They hang on signals, not on calls — that is the difference from
 * before, where every change had to remember to call `renderOverlay()`.
 */

import { useRef, useEffect, useLayoutEffect } from 'preact/hooks'
import type { RefObject } from 'preact'
import { useSignal, useSignalEffect } from '@preact/signals'
import * as st from '../state.js'
import * as text from '../editor/text.js'
import * as geo from '../editor/geometry.js'
import { bucket, paintHighlights, measureWedges, type Wedge } from '../editor/marks.js'
import { insertionAt, isCommaFinding } from '../spell/rules.js'
import type { Finding } from '../types.js'

export function Editor({
  inputRef,
  onInput,
  onHelp,
  fontFamily,
  readOnly = false,
}: {
  /**
   * Looking, not writing — a parent's view of the child's text. The field
   * stays a field, with its marks and its help, but takes no keystrokes.
   */
  readOnly?: boolean
  /**
   * The writing field, handed outwards.
   *
   * As an ordinary property and not as a `ref`: a function component takes no
   * ref, and the app needs the element — it reads the text from it and sets
   * the caret.
   */
  inputRef: RefObject<HTMLDivElement>
  /** After every input: save and check. The editor does not do that itself. */
  onInput: (e: InputEvent) => void
  /** Open the help for a finding. */
  onHelp: (hit: Finding) => void
  /** The family currently being written in — for the font measurement. */
  fontFamily: string
}) {
  const input = inputRef
  const stack = useRef<HTMLDivElement>(null)
  const wrap = useRef<HTMLElement>(null)
  const gaps = useRef<HTMLDivElement>(null)

  /** Where the wedges belong. Measured after the text has been laid out. */
  const wedges = useSignal<Wedge[]>([])
  /** The scaling-down factor from fitSheet, which the wedges calculate back. */
  const scale = useRef(1)

  // --- Measuring ------------------------------------------------------------

  /**
   * The whole chain: fit in, lay the baseline, count the sheets, place the
   * wedges.
   *
   * The order matters and used to stand identically in four places — here once.
   * `useLayoutEffect`, so that nothing stands wrong for the length of a frame.
   */
  const measure = (): void => {
    const el = input.current
    const st_ = stack.current
    const wr = wrap.current
    if (!el || !st_ || !wr) return

    scale.current = geo.fitSheet(st_, wr)

    const size = geo.fontPx(el)
    const line = geo.linePx(el)
    const m = geo.measureFont(size, fontFamily)
    if (m) {
      const b = geo.baselineFor(st.currentPaper.value, line, m)
      if (b) geo.applyBaseline(b)
    }

  }

  // Again on rotation and on every change of paper or font.
  useLayoutEffect(() => {
    measure()
    addEventListener('resize', measure)
    return () => removeEventListener('resize', measure)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fontFamily, st.paper.value, st.gridSpacing.value])

  /**
   * Measure again whenever the text gets taller or shorter.
   *
   * The list above names the OCCASIONS, and a text put on the paper is not
   * among them — opening a saved one left the measurements at whatever they
   * were.
   *
   * Naming that case as well would fix it and leave the next one. What is
   * really being watched is the HEIGHT of the text, so that is what is
   * observed: loading, syncing down from another device, a pasted paragraph,
   * a line that rewraps — all of them arrive here without being listed.
   */
  useLayoutEffect(() => {
    const el = input.current
    if (!el || typeof ResizeObserver !== 'function') return
    const ro = new ResizeObserver(() => measure())
    ro.observe(el)
    return () => ro.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * The font must BE THERE before the measuring.
   *
   * Otherwise one measures the system font, lays the baseline by it, and the
   * text jumps as soon as the right font arrives. In the old `init()` that was
   * the reason for the second `layoutPages()` pass.
   */
  useEffect(() => {
    let alive = true
    const el = input.current
    if (!el) return
    document.fonts
      .load(`${geo.fontPx(el)}px "${fontFamily}"`)
      .then(() => document.fonts.ready)
      .then(() => { if (alive) measure() })
      .catch(() => {})
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fontFamily])

  // --- Marks ----------------------------------------------------------------

  /**
   * The waves and the wedges.
   *
   * Hangs on everything that moves the marks: the findings, the open help, the
   * green, the comma switch. Before, every one of these changes had to
   * remember to call `renderOverlay()` — one forgotten place left old waves
   * behind, and nothing said anything.
   */
  useSignalEffect(() => {
    const el = input.current
    const layer = gaps.current
    if (!el || !layer) return

    const b = bucket(st.errors.value, {
      activeStart: st.session.value?.start ?? null,
      solved: st.solved.value,
      commas: st.commas.value,
      insertionAt,
      isCommaFinding,
    })

    paintHighlights(el, b)
    wedges.value = measureWedges(el, layer, b.gaps, {
      scale: scale.current,
      fontPx: geo.fontPx(el),
      fontFamily,
      commas: st.commas.value,
      insertionAt,
      isCommaFinding,
    })
  })

  // --- Operation ------------------------------------------------------------

  /**
   * Finds the finding under the pointer.
   *
   * Earlier there were <mark> elements in the overlay that could be queried.
   * Now the Highlight API draws, and it creates no elements — so the search
   * goes through the rectangles of the finding ranges.
   */
  const markAt = (x: number, y: number): Finding | null => {
    const el = input.current
    if (!el) return null
    for (const e of st.errors.value) {
      // Search where the mark was DRAWN, not where the finding stands. The
      // wedge of a missing space sits at the joint — a right-click on the
      // characters on both sides of it must find it, otherwise one would have
      // to hit a place of zero width.
      const place = insertionAt(e)
      const at = place ? e.start + place.offset : 0
      const r = place
        ? text.rangeFor(el, Math.max(0, at - 1),
                        Math.min(text.getText(el).length, at + 1))
        : text.rangeFor(el, e.start, e.end)
      for (const rect of r.getClientRects()) {
        // A little room around the word.
        //
        // The squiggle is drawn BELOW the letters and is what one aims at —
        // it sits outside the text rectangle, so a click that plainly hits
        // the mark found nothing at all. The same at the sides: whoever
        // clicks at the very end of a short word like „is" misses it by a
        // pixel, and nothing happens, with no way of telling why.
        //
        // Four pixels: enough for the wave and for an unsteady hand, little
        // enough that two marked words next to each other stay apart.
        const PAD = 4
        if (x >= rect.left - PAD && x <= rect.right + PAD &&
            y >= rect.top - PAD && y <= rect.bottom + PAD) {
          return e
        }
      }
    }
    return null
  }

  return (
    <main ref={wrap} class="sheet-wrap">
      <div ref={stack} class="stack">
        {/* The sheets. Each one is true DIN A4. They lie BEHIND the writing
            area and carry only paper, rules and margin — the text flows
            across on top of them. */}
        {/* ONE sheet on screen, however long the text.
            
            It used to be a stack of A4 sheets, and a boundary fell wherever
            the 297 mm happened to end — mid-sentence, for no reason a child
            writing would care about. A screen scrolls; it does not turn
            pages.
            
            Printing is where paper becomes pages: `@media print` below gives
            the sheet its A4 height back and lets the browser paginate. */}
        <div class="pages" aria-hidden="true">
          <div class="page">
            <div class="page-rules" />
          </div>
        </div>

        {/* Wedges for missing commas and spaces. They mark a PLACE, not a
            character, and therefore cannot be drawn by the Highlight API —
            that one styles text. The layer carries no text of its own and
            takes no clicks, so caret, selection and undo stay untouched. */}
        <div ref={gaps} class="gaps" aria-hidden="true">
          {wedges.value.map((w, i) => (
            <i
              key={i}
              class={w.quiet ? 'gap-mark quiet' : 'gap-mark'}
              style={{ left: `${w.left}px`, top: `${w.top}px` }}
            />
          ))}
        </div>

        <div class="sheet">
          <div
            ref={input}
            id="input"
            contentEditable={readOnly ? 'false' : 'plaintext-only'}
            role="textbox"
            aria-multiline="true"
            aria-label="Dein Text"
            spellcheck={false}
            autocorrect="off"
            autocapitalize="off"
            translate={false}
            class="notranslate"
            data-gramm="false"
            data-gramm_editor="false"
            data-enable-grammarly="false"
            data-placeholder="Schreib hier los …"
            /* Keeps the DOM flat so the offsets keep working: a paragraph
               would nest a <div> and shift every offset behind it. */
            onBeforeInput={(e) => {
              const ev = e as InputEvent
              if (ev.inputType === 'insertParagraph') {
                ev.preventDefault()
                document.execCommand('insertLineBreak')
              }
            }}
            onInput={(e) => {
              onInput(e as unknown as InputEvent)
              measure()
            }}
            /* The help comes through the context menu, not through a click.

               A normal click sets the caret — and it must, otherwise one can
               no longer move and correct freely in one's own text. A card
               that jumps up every time stands in the way while writing.
               Whoever wants help fetches it on purpose. */
            onContextMenu={(e) => {
              const hit = markAt(e.clientX, e.clientY)
              if (!hit) return // otherwise the browser menu stays reachable
              e.preventDefault()
              onHelp(hit)
            }}
            onMouseDown={() => {
              if (st.session.value) st.session.value = null
            }}
            // Keyboard way to the help: caret in the word, then Ctrl+Space.
            // Reachable without a mouse, and faster than clicking there.
            onKeyDown={(e) => {
              const el = input.current
              if (!el) return
              if (e.key === ' ' && (e.ctrlKey || e.metaKey)) {
                const pos = text.caret(el)?.start ?? 0
                const hit =
                  st.errors.value.find((x) => pos >= x.start && pos <= x.end) ??
                  st.errors.value[0]
                if (hit) { e.preventDefault(); onHelp(hit) }
              }
              if (e.key === 'Escape' && st.session.value) st.session.value = null
            }}
          />
        </div>
      </div>
    </main>
  )
}
